import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useUnarchiveClient } from '../../hooks';
import { Client } from '../../types';

interface UnarchiveClientModalProps {
  client: Client;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A streamlined client unarchiving modal for restoring archived clients to active status.
 * Provides simple confirmation workflow with clear messaging about restoration process.
 *
 * **Key Features:**
 * - **Simple Confirmation**: Single-click unarchive with confirmation dialog
 * - **Dual State Management**: Supports both internal and external modal state control
 * - **Clear Messaging**: Explains what happens when unarchiving clients
 * - **API Integration**: Handles async unarchive operations with proper error handling
 * - **Data Consistency**: Invalidates relevant query caches after successful unarchiving
 * - **Flexible Triggers**: Supports custom triggers or falls back to default button
 * - **Loading States**: Shows proper loading feedback during API operations
 *
 * **Unarchive Process:**
 * 1. User confirms unarchive action
 * 2. API call restores client to active status
 * 3. Success invalidates client caches and closes modal
 * 4. Error keeps modal open for retry
 *
 * **Restoration Effects:**
 * - Client becomes visible in active client lists
 * - Client management functionality is restored
 * - All historical data remains intact
 * - Projects and billing associations are restored
 *
 * @example
 * ```tsx
 * // Basic usage with internal state management
 * <UnarchiveClientModal
 *   client={archivedClient}
 *   onSuccess={() => {
 *     toast.success('Client restored successfully');
 *     navigate('/clients');
 *   }}
 * />
 *
 * // With custom trigger button
 * <UnarchiveClientModal
 *   client={client}
 *   trigger={
 *     <Button variant="outline" size="sm">
 *       <ArchiveRestore className="w-4 h-4 mr-2" />
 *       Restore Client
 *     </Button>
 *   }
 *   onSuccess={() => queryClient.invalidateQueries(['clients'])}
 * />
 *
 * // In archived clients list
 * function ArchivedClientsList() {
 *   const { data: archivedClients } = useQuery(['clients', 'archived'], getArchivedClients);
 *
 *   return (
 *     <div className="space-y-4">
 *       {archivedClients?.map(client => (
 *         <div key={client.id} className="flex items-center justify-between p-4 border rounded">
 *           <div>
 *             <h3>{client.name}</h3>
 *             <p className="text-sm text-muted-foreground">
 *               Archived on {format(client.archivedAt, 'MMM dd, yyyy')}
 *             </p>
 *           </div>
 *           <UnarchiveClientModal
 *             client={client}
 *             onSuccess={() => {
 *               toast.success(`${client.name} restored`);
 *               queryClient.invalidateQueries(['clients']);
 *             }}
 *           />
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 *
 * // External state management for complex workflows
 * function ClientManagement() {
 *   const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
 *   const [selectedClient, setSelectedClient] = useState<Client | null>(null);
 *
 *   const handleUnarchiveClick = (client: Client) => {
 *     setSelectedClient(client);
 *     setShowUnarchiveModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <ArchivedClientsList onUnarchive={handleUnarchiveClick} />
 *
 *       {selectedClient && (
 *         <UnarchiveClientModal
 *           client={selectedClient}
 *           open={showUnarchiveModal}
 *           onOpen={() => setShowUnarchiveModal(true)}
 *           onClose={() => {
 *             setShowUnarchiveModal(false);
 *             setSelectedClient(null);
 *           }}
 *           onSuccess={() => {
 *             toast.success('Client restored to active status');
 *             setShowUnarchiveModal(false);
 *             setSelectedClient(null);
 *             queryClient.invalidateQueries(['clients']);
 *           }}
 *         />
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function UnarchiveClientModal({
  client,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: UnarchiveClientModalProps) {
  const { t } = useTranslation(['authenticated.clients', 'common']);

  const { mutate, isPending } = useUnarchiveClient({
    clientId: client.id,
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
  });

  const handleUnarchive = () => {
    mutate();
  };

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.clients:archive.unarchiveClientTitle')}
      message={t('authenticated.clients:archive.unarchiveClientMessage')}
      confirmText={
        isPending
          ? t('authenticated.clients:archive.unarchiving')
          : t('authenticated.clients:archive.unarchive')
      }
      confirmVariant="default"
      onConfirm={handleUnarchive}
      isLoading={isPending}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    />
  );
}
